import $ from 'jquery';
import _ from 'underscore';
import popupTemplate from 'bloko/blocks/popup/popup.mustache';
import Modal from 'bloko/blocks/modal/modal';
import blokoEvent from 'bloko/common/event';
import Components from 'bloko/common/core/Components';

const BLOCK_NAME = 'popup';

const cssClasses = {
    size: {
        min: 'popup_min',
        mid: 'popup_mid',
        big: 'popup_big',
        max: 'popup_max',
        full: 'popup_full',
        'by-content': 'popup_size-by-content',
    },
};

/**
 * @param {Element} element
 * @param {Object} options
 * @param {String|Array} [options.classnames]  CSS-классы попапа, по которым к нему можно обратиться из других
 *                                             компонентов. Задать классы можно несколькими способами:
 *                                             либо перечисляя классы в массиве, либо строкой через пробел.
 *                                             Можно задать один класс
 * @param {Boolean} [options.closebutton=true] Показывать ли закрывашку и разрешить закрытие попапа при клике
 *                                             на оверлее и нажатии клавиши Esc
 * @param {String} [options.size='mid']        Размер попапа. Допустимые значения:
 * * `min` (360px)
 * * `mid` (580px)
 * * `big` (620px)
 * * `max` (740px)
 * * `full` (90% ширины экрана)
 * * `by-content` (По ширине контента. Если контент не помещается во viewport, ширина попапа занимает доступную
 * ширину. Нужно учитывать, что контейнер растягивается на 100% внутри попапа)
 * @constructor
 */
function Popup(element, options) {
    const $element = $(element);
    let classNames = options.classnames || [];
    let $content;
    let closeBy = {};

    if (!options.closebutton) {
        closeBy = {
            closeButtonClick: false,
            backgroundClick: false,
            escapePress: false,
        };
    }

    const $popupContainer = $(popupTemplate.render());
    const $popupContent = $('[data-attach="popup-content"]', $popupContainer);
    const $popupHeader = $popupContainer.find('[data-attach="popup-header"]');
    const $popupTitle = $popupContainer.find('[data-attach="popup-header-text"]');

    const modal = Components.make(Modal, $popupContainer.get(0), { closeBy });

    $popupContent.on('click', `[data-attach="${BLOCK_NAME}-hide"]`, hide);

    modal.on('hid', () => {
        _getContent().addClass('g-hidden');
        $popupContainer.detach();
        $element.append(_getContent(), null);
        element.dispatchEvent(blokoEvent('hidPopup'));
    });

    modal.on('showed', () => {
        element.dispatchEvent(blokoEvent('showedPopup'));
    });

    function _isVisible() {
        return $popupContent && $popupContent.is(':visible');
    }

    function _hasContent() {
        return _getContent().length > 0;
    }

    function _getContent() {
        if ($content) {
            return $content;
        }

        $content = $element.contents();
        return $content;
    }

    /**
     * Обновляет параметры, анимации, заголовки
     * @private
     */
    function _updateStyle() {
        const size = options.size;
        const title = $element.data('title');

        $popupContent.removeClass(_.values(cssClasses.size).join(' '));
        if (size) {
            $popupContent.addClass(cssClasses.size[size]);
        }

        updateTitle(title);
    }

    function _setContent() {
        $popupContent.empty().append(_getContent());

        _getContent().removeClass('g-hidden');
        _updateStyle();
    }

    /**
     * Показать попап
     * @fires showedpopup
     */
    function show() {
        if (!_hasContent()) {
            return;
        }

        if (_isVisible()) {
            return;
        }

        _setContent();
        modal.show();
    }

    /**
     * Скрыть попап
     * @fires hidpopup
     */
    function hide() {
        modal.hide();
    }

    /**
     * Обновить заголовок попапа
     * @param {String} title
     * @private
     */
    function updateTitle(title) {
        const hasTitle = title && title.length > 0;

        if (hasTitle) {
            $popupTitle.text(title);
        }

        $popupHeader.toggleClass('g-hidden', !hasTitle);
    }

    /**
     * Изменить заголовок попапа
     * @param {String} title
     */
    function setTitle(title) {
        $element.data('title', title);
        updateTitle(title);
    }

    if (typeof classNames === 'string') {
        classNames = classNames.trim().split(/\s+/);
    }
    $element.addClass(classNames.join(' '));

    return {
        show,
        hide,
        setTitle,
    };
}

/**
 * @namespace
 * @property {boolean} closebutton=true  - добавлять кнопку для закрытия попапа
 * @property {string}  size='mid'        - размер попапа ('min', 'mid', 'big', 'max', 'full')
 */
export default Components.build({
    defaults: {
        closebutton: true,
        size: 'mid',
    },
    create(element, options) {
        return new Popup(element, options);
    },
});
